<template>
  <SettingsBox
    title="Ustawienia zaawansowane"
    description="Dostosuj jadłospis do Twoich indywidualnych potrzeb."
  >
    <div class="advanced-settings">
      <div v-if="showRepeatedDishesSettings" class="advanced-settings__row">
        <button
          v-if="isBasicPlan"
          aria-label="Odblokuj potrawy powtórzone w bieżącym jadłospisie"
          class="advanced-settings__upgrade-overlay"
          type="button"
          @click="globalStore.showAppUpgradePlanModal"
        />
        <div class="advanced-settings__row-content">
          <Switch
            :disabled="isBasicPlan"
            label="Potrawy powtórzone w bieżącym jadłospisie"
            :model-value="repeatedCurrentDietActive"
            size="md"
            @update:model-value="updateRepeatedCurrentDietActive"
          />
          <Text as="p" color="color-text-text-tertiary-600" size="text-sm">
            Podświetla potrawy, które powtarzają się w jadłospisie nad którym aktualnie pracujesz.
          </Text>
        </div>
      </div>

      <div v-if="showRepeatedDishesSettings" class="advanced-settings__row">
        <button
          v-if="isBasicPlan"
          aria-label="Odblokuj potrawy powtórzone w poprzednich jadłospisach"
          class="advanced-settings__upgrade-overlay"
          type="button"
          @click="globalStore.showAppUpgradePlanModal"
        />
        <div class="advanced-settings__row-content">
          <div class="advanced-settings__label">
            <Switch
              :disabled="isBasicPlan"
              label="Potrawy powtórzone w poprzednich jadłospisach"
              :model-value="repeatedPreviousDietsActive"
              size="md"
              @update:model-value="updateRepeatedPreviousDietsActive"
            />
            <Tooltip placement="top">
              <template #trigger><Icon name="HelpCircle" :size="16" /></template>
              System bierze pod uwagę ostatnie jadłospisy pacjenta według daty ich utworzenia –
              niezależnie od tego, czy mają przypisane daty w kalendarzu.
            </Tooltip>
          </div>
          <Text as="p" color="color-text-text-tertiary-600" size="text-sm">
            Podświetla potrawy, które wystąpiły w wybranej liczbie poprzednich jadłospisów pacjenta.
          </Text>
        </div>
        <Select
          class="advanced-settings__select"
          aria-label="Liczba poprzednich jadłospisów"
          :disabled="isBasicPlan || !repeatedPreviousDietsActive"
          :items="dietsCountItems"
          :model-value="repeatedDietsCount"
          size="sm"
          @update:model-value="updateRepeatedDietsCount"
        />
      </div>

      <div class="advanced-settings__row">
        <div class="advanced-settings__row-content">
          <Tooltip placement="top">
            <template #trigger>
              <Switch
                label="Straty talerzowe"
                :model-value="active"
                size="md"
                @update:model-value="emit('active', $event)"
              />
            </template>
            Są to resztki talerzowe i kotłowe oraz odpadki kuchenne.
          </Tooltip>
          <Text as="p" color="color-text-text-tertiary-600" size="text-sm">
            Redukuje o wybrany procent wartość energetyczną i składniki odżywcze w jadłospisie.
          </Text>
        </div>
        <Input
          test-id="DietDetailSettings__InevitableLosses"
          class="advanced-settings__losses"
          :disabled="!active"
          :min="0.1"
          :max="99"
          :step="0.1"
          :model-value="value"
          type="number"
          @update:model-value="emit('value', $event)"
        >
          <template #suffix>%</template>
        </Input>
      </div>
    </div>
  </SettingsBox>
</template>
<script lang="ts" setup>
  import { computed } from 'vue'

  import { Icon, Input, Select, Switch, Text, Tooltip } from '@/components/ui'
  import { useGlobalStore } from '@/store/globalStore'

  import { SettingsBox } from '../SettingsBox'

  const globalStore = useGlobalStore()
  const props = defineProps<{
    active: boolean
    repeatedCurrentDietActive: boolean
    repeatedDietsCount: number
    repeatedPreviousDietsActive: boolean
    showRepeatedDishesSettings: boolean
    value: number | string
  }>()

  const isBasicPlan = computed(
    () => globalStore.user?.current_plan_name.trim().toLowerCase() === 'basic',
  )
  const dietsCountItems = Array.from({ length: 10 }, (_, index) => ({
    id: index + 1,
    label: String(index + 1),
  }))
  const emit = defineEmits<{
    active: [value: boolean]
    'repeated-current-diet-active': [value: boolean]
    'repeated-diets-count': [value: number]
    'repeated-previous-diets-active': [value: boolean]
    value: [value: string]
  }>()
  const updateRepeatedCurrentDietActive = (value: boolean) => {
    if (!isBasicPlan.value) emit('repeated-current-diet-active', value)
  }
  const updateRepeatedPreviousDietsActive = (value: boolean) => {
    if (!isBasicPlan.value) emit('repeated-previous-diets-active', value)
  }
  const updateRepeatedDietsCount = (value: number | string) => {
    if (!isBasicPlan.value) emit('repeated-diets-count', Number(value))
  }
</script>

<style scoped lang="scss">
  .advanced-settings__row {
    display: flex;
    gap: 24px;
    align-items: center;
    justify-content: space-between;
    padding: 16px 0;
    border-bottom: 1px solid var(--color-border-border-secondary);
    position: relative;
  }

  .advanced-settings__upgrade-overlay {
    background: transparent;
    border: 0;
    cursor: pointer;
    inset: 0;
    padding: 0;
    position: absolute;
    z-index: 1;
  }

  .advanced-settings__row:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }

  .advanced-settings__row-content {
    min-width: 0;
  }

  .advanced-settings__row-content > p {
    margin: 4px 0 0 52px;
  }

  .advanced-settings__label {
    display: flex;
    gap: 4px;
    align-items: center;
  }

  .advanced-settings__label :deep(.ui-icon) {
    color: var(--color-foreground-fg-quaternary-500);
  }

  .advanced-settings__select  {
    flex: 0 0 84px;
    width: 84px;
  }

  .advanced-settings__losses {
    flex: 0 0 120px;
    width: 120px;
  }

</style>
